import {InformationCircleIcon} from '@heroicons/react/24/outline';
import React from 'react';

import {Site} from 'src/lib/api/project';
import {
  NDSIHistoricalColumns,
  NDSIObservationColumns,
  NDVIHistoricalColumns,
  NDVIObservationColumns,
  SWEHistoricalColumns,
  SWEObservationColumns,
  Source,
} from 'src/lib/api/series';
import COLORS from 'src/lib/colors';

import TimeSeriesProvider from './TimeSeriesProvider';
import TimeSeriesViz, {STACKED_VIZ_HEIGHT, STACKED_VIZ_HEIGHT_WITH_LEGEND} from './TimeSeriesViz';
import {Translator, useTranslations} from './TranslationsContext';
import Viz from './Viz';
import {
  VizConfig,
  filterConfigToAvailableTimeSeries,
  makeConfigItems,
  makeRequestFromVizConfig,
} from './Viz/config';
import WithWidth from './WithWidth';

function makeConfig(t: Translator, includeFlowContributingAreas: boolean): VizConfig {
  return {
    forecasts: [],
    observations: makeConfigItems(
      'observations',
      [
        // SWE
        {
          source: Source.SNODAS_HISTORICAL_DAILY,
          columns: [SWEHistoricalColumns.SWE_0_95, SWEHistoricalColumns.SWE_0_05],
          displayName: `SNODAS ${t('historical')} 90% CI`,
          getStyle: () => ({
            fill: COLORS.SNOW_RANGE,
          }),
          legendIcon: 'rect',
          groupId: t('snowWaterEquivalent'),
        },
        {
          source: Source.SNODAS_HISTORICAL_DAILY,
          columns: [SWEHistoricalColumns.SWE_MEAN],
          displayName: `SNODAS ${t('historicalMean')}`,
          getStyle: () => ({
            stroke: COLORS.SNOW_LTA,
            strokeDasharray: 3,
          }),
          legendIcon: 'line',
          groupId: t('snowWaterEquivalent'),
        },
        {
          source: Source.SNODAS,
          columns: [SWEObservationColumns.SWE],
          displayName: `SNODAS ${t('observed')}`,
          getStyle: () => ({
            stroke: COLORS.OBS,
          }),
          legendIcon: 'line',
          groupId: t('snowWaterEquivalent'),
        },
        {
          source: Source.SWANN,
          columns: [SWEObservationColumns.SWE],
          displayName: `SWANN ${t('observed')}`,
          getStyle: () => ({
            stroke: COLORS.SWANN_OBS,
          }),
          legendIcon: 'line',
          groupId: t('snowWaterEquivalent'),
        },
        // NDSI
        {
          source: Source.MODIS_NDSI_HISTORICAL_DAILY,
          columns: [NDSIHistoricalColumns.NDSI_0_95, NDSIHistoricalColumns.NDSI_0_05],
          displayName: `${t('historical')} 90% CI`,
          getStyle: () => ({
            fill: COLORS.SNOW_RANGE,
          }),
          legendIcon: 'rect',
          groupId: t('snowCover'),
        },
        {
          source: Source.MODIS_NDSI_HISTORICAL_DAILY,
          columns: [NDSIHistoricalColumns.NDSI_MEAN],
          displayName: t('historicalMean'),
          getStyle: () => ({
            stroke: COLORS.SNOW_LTA,
            strokeDasharray: 3,
          }),
          legendIcon: 'line',
          groupId: t('snowCover'),
        },
        {
          source: Source.MODIS_NDSI,
          columns: [NDSIObservationColumns.NDSI_MEDIAN],
          displayName: `${t('satellite')} ${t('observed')}`,
          getStyle: () => ({
            stroke: COLORS.OBS,
          }),
          legendIcon: 'line',
          groupId: t('snowCover'),
        },
        // NDVI
        {
          source: Source.MODIS_NDVI_HISTORICAL_DAILY,
          columns: [NDVIHistoricalColumns.NDVI_0_95, NDVIHistoricalColumns.NDVI_0_05],
          displayName: `${t('historical')} 90% CI`,
          getStyle: () => ({
            fill: COLORS.VEG_RANGE,
          }),
          legendIcon: 'rect',
          groupId: t('vegetation'),
        },
        {
          source: Source.MODIS_NDVI_HISTORICAL_DAILY,
          columns: [NDVIHistoricalColumns.NDVI_MEAN],
          displayName: t('historicalMean'),
          getStyle: () => ({
            stroke: COLORS.VEG_LTA,
            strokeDasharray: 3,
          }),
          legendIcon: 'line',
          groupId: t('vegetation'),
        },
        {
          source: Source.MODIS_NDVI,
          columns: [NDVIObservationColumns.NDVI_MAX],
          displayName: `${t('satellite')} ${t('observed')}`,
          getStyle: () => ({
            stroke: COLORS.OBS,
          }),
          legendIcon: 'line',
          groupId: t('vegetation'),
        },
      ],
      {includeFlowContributingAreas}
    ),
  };
}

type VizGroup = {
  id: string;
  minMaxRangeConstraints?: [number | null, number | null];
};

function makeVizGroups(t: Translator): VizGroup[] {
  return [
    {
      id: t('snowWaterEquivalent'),
      minMaxRangeConstraints: [null, 0.1],
    },
    {
      id: t('snowCover'),
    },
    {
      id: t('vegetation'),
    },
  ];
}

export default function Surface({
  site,
  dateRange,
  includeFlowContributingAreas,
  renderOptions,
}: {
  site: Site;
  dateRange: [Date, Date];
  includeFlowContributingAreas: boolean;
  renderOptions?: () => React.ReactNode;
}) {
  const {t} = useTranslations();
  const config = filterConfigToAvailableTimeSeries(
    makeConfig(t, includeFlowContributingAreas),
    site
  );

  const vizGroups = makeVizGroups(t).filter(
    ({id}) => config.observations.findIndex((c) => c.groupId === id) > -1
  );

  return (
    <WithWidth>
      {(width) => (
        <TimeSeriesProvider
          site={site}
          forecastTimesBySource={{}}
          dateRange={dateRange}
          {...makeRequestFromVizConfig(config)}
        >
          {(vizProps) => {
            return (
              <Viz
                section={t('surfaceObservations')}
                title={t('surfaceObservations')}
                renderOptions={renderOptions}
                site={site}
                loadingStateDims={[width, STACKED_VIZ_HEIGHT_WITH_LEGEND * vizGroups.length]}
                dateRange={dateRange}
                config={config}
                {...vizProps}
              >
                {(props) => {
                  const warningsByGroup: Record<string, string[]> = {};
                  const groupHasData: Record<string, boolean> = {};
                  for (const ts of props.observationTimeSeriesList ?? []) {
                    const configItem = config.observations.find(
                      (c) => c.source === ts.query.source
                    );
                    if (!configItem?.groupId) continue;
                    if (ts.timestamps.length > 0) {
                      groupHasData[configItem.groupId] = true;
                    }
                    for (const warning of ts.warnings ?? []) {
                      (warningsByGroup[configItem.groupId] ??= []).push(warning);
                    }
                  }

                  const activeVizGroups = vizGroups.filter(({id}) => groupHasData[id]);

                  return (
                    <>
                      {activeVizGroups.map(({id, minMaxRangeConstraints}) => (
                        <div key={id}>
                          <TimeSeriesViz
                            {...props}
                            timeZone={site.localTimeZone}
                            groupId={id}
                            dims={[width, STACKED_VIZ_HEIGHT]}
                            range={[0, 'auto']}
                            minMaxRangeConstraints={minMaxRangeConstraints}
                          />
                          {warningsByGroup[id]?.length > 0 && (
                            <p className="text-xs text-gray-400 mt-1 mb-2 flex items-center justify-center gap-1">
                              <InformationCircleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                              {warningsByGroup[id].join('; ')}
                            </p>
                          )}
                        </div>
                      ))}
                    </>
                  );
                }}
              </Viz>
            );
          }}
        </TimeSeriesProvider>
      )}
    </WithWidth>
  );
}
